import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  FormHelperText,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import useStatusToggle from '../../hooks/useStatusToggle';
import StatusSwitch from '../../sub_components/status_switch';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSection,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';
import {
  REDEMPTION_TYPES,
  TYPE_SUBTITLES,
  REDEMPTION_VALUE_LABELS,
  emptyRedemptionForm,
  usesField,
  changeRedemptionType,
  validateRedemptionForm,
  buildRedemptionPayload,
  redemptionToForm,
  apiFieldErrors,
} from './redemptionRuleForm';

const emptyConfigForm = {
  inventory: '',
  // Off until an admin switches it on: saving a draft config must not start
  // showing loyalty to that hub's customers.
  is_enabled: false,
  base_percentage: '1',
  point_value: '1',
  redemption_point_value: '1',
  allow_redemption_on_products: true,
  allow_redemption_on_subscriptions: false,
  min_order_amount: '0',
  max_points_per_order: '',
  expiry_days: '',
  welcome_bonus_enabled: false,
  welcome_bonus_points: '0',
};

/**
 * Hub loyalty earn + redemption config (maps to BE ViewSets:
 * loyalty-config, loyalty-redemption-config).
 * Subscription loyalty multiplier / random offer / free delivery stay on Subscription Plans.
 */
export function LoyaltyConfig() {
  const dispatch = useDispatch();
  const statusToggle = useStatusToggle();
  // Its own instance: config and rule ids are separate sequences, so config #3
  // and rule #3 must not share one "pending" row.
  const ruleStatusToggle = useStatusToggle();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [redemptions, setRedemptions] = useState([]);

  const [configModal, setConfigModal] = useState(false);
  const [editConfig, setEditConfig] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [configForm, setConfigForm] = useState(emptyConfigForm);

  const [redemptionModal, setRedemptionModal] = useState(false);
  const [editRedemption, setEditRedemption] = useState(false);
  const [selectedRedemptionId, setSelectedRedemptionId] = useState(null);
  const [redemptionForm, setRedemptionForm] = useState(emptyRedemptionForm);
  const [redemptionErrors, setRedemptionErrors] = useState({});
  const [offerOptions, setOfferOptions] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    dispatch(updatepageName('loyalty_config'));
    fetchConfigs();
    fetchRedemptions();
  }, [dispatch]);

  // Offers are per hub, so they follow the selected loyalty config.
  const redemptionHub = configs.find(
    (c) => Number(c.id) === Number(redemptionForm.loyalty_config)
  )?.inventory;
  const selectedRedemption = editRedemption
    ? redemptions.find((r) => Number(r.id) === Number(selectedRedemptionId))
    : null;
  const needsOffers = redemptionModal && usesField(redemptionForm.redemption_type, 'offer');
  // Free Product maps a loyalty product: an offer of type "loyalty offer" from
  // Offer Management. Offer Redemption lists the hub's other offers.
  const mapsLoyaltyProduct = redemptionForm.redemption_type === 'PRODUCT';
  const pickableOffers = offerOptions.filter(
    (o) => (o.offer_type === 'loyalty offer') === mapsLoyaltyProduct
  );
  const needsProducts = redemptionModal && usesField(redemptionForm.redemption_type, 'product');

  useEffect(() => {
    if (!needsOffers || !redemptionHub) {
      setOfferOptions([]);
      setOffersLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOffersLoading(true);
    CustomAxios.get(`offers/${redemptionHub}/`)
      .then((res) => {
        if (!cancelled) setOfferOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) showToast('error', 'Error', 'Failed to load offers for this hub');
      })
      .finally(() => {
        if (!cancelled) setOffersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsOffers, redemptionHub]);

  useEffect(() => {
    if (!needsProducts || productOptions.length) return;
    setProductsLoading(true);
    CustomAxios.get('products_admin/')
      .then((res) => setProductOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => showToast('error', 'Error', 'Failed to load products'))
      .finally(() => setProductsLoading(false));
  }, [needsProducts, productOptions.length]);

  const updateRedemptionForm = (patch) => {
    setRedemptionForm((prev) => ({ ...prev, ...patch }));
    setRedemptionErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const onRedemptionTypeChange = (type) => {
    setRedemptionForm((prev) => changeRedemptionType(prev, type));
    setRedemptionErrors({});
  };

  // Validation state for one field, spread onto a TextField / FormControl.
  const fieldError = (field, helperText) => ({
    error: !!redemptionErrors[field],
    helperText: redemptionErrors[field] || helperText,
  });

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubLabel = (inventoryId) => {
    const hub = inventoryList.find((h) => Number(h.value ?? h.id) === Number(inventoryId));
    if (!hub) return `Hub #${inventoryId}`;
    return hub.label || hub.place || hub.name || `Hub #${inventoryId}`;
  };

  /*
   * Is the hub this rule belongs to switched on?
   *
   * A rule can be Active while its hub's loyalty is off, and then no customer
   * sees it: the rules API filters by the hub's enabled config, and every
   * redemption path checks the same switch. The rule list used to say "Active"
   * either way, which is the one thing it could say that was not true.
   */
  const configEnabled = (configId) => {
    const cfg = configs.find((c) => Number(c.id) === Number(configId));
    return cfg ? cfg.is_enabled !== false : true;
  };

  /*
   * Why an Active rule is still not offered to customers, or null.
   *
   * The rules API already judges this (`is_available` / `unavailable_reason`)
   * with the same check checkout uses -- validity, limits, and for a Free
   * Product whether this hub actually sells it. The app hides a rule the server
   * calls unavailable, so an admin saw "Active" on a reward nobody could see.
   */
  const hiddenFromCustomersReason = (row) => {
    if (!row?.is_active || row.is_available !== false) return null;
    if (row.redemption_type === 'PRODUCT') {
      const cfg = configs.find((c) => Number(c.id) === Number(row.loyalty_config));
      const hub = cfg ? hubLabel(cfg.inventory) : 'this hub';
      return (
        `Not shown to customers: ${row.product_name || 'the selected product'} is not on sale at ${hub}. ` +
        'Give it an available, priced capacity at this hub, or choose a product this hub sells.'
      );
    }
    return `Not shown to customers: ${row.unavailable_reason || 'currently unavailable'}`;
  };

  const configLabel = (configId) => {
    const cfg = configs.find((c) => Number(c.id) === Number(configId));
    if (!cfg) return `Config #${configId}`;
    return `${hubLabel(cfg.inventory)} (#${cfg.id})`;
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('loyalty-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load loyalty configs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRedemptions = async () => {
    try {
      const res = await CustomAxios.get('loyalty-redemption-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setRedemptions(data);
    } catch (e) {
      showToast('error', 'Error', 'Failed to load redemption configs');
    }
  };

  const openConfigModal = (item = null) => {
    if (item) {
      setEditConfig(true);
      setSelectedConfigId(item.id);
      setConfigForm({
        inventory: item.inventory ?? '',
        is_enabled: !!item.is_enabled,
        base_percentage: String(item.base_percentage ?? '1'),
        point_value: String(item.point_value ?? '1'),
        redemption_point_value: String(item.redemption_point_value ?? '1'),
        allow_redemption_on_products: item.allow_redemption_on_products !== false,
        allow_redemption_on_subscriptions: !!item.allow_redemption_on_subscriptions,
        min_order_amount: String(item.min_order_amount ?? '0'),
        max_points_per_order: item.max_points_per_order != null ? String(item.max_points_per_order) : '',
        expiry_days: item.expiry_days != null ? String(item.expiry_days) : '',
        welcome_bonus_enabled: !!item.welcome_bonus_enabled,
        welcome_bonus_points: String(item.welcome_bonus_points ?? '0'),
      });
    } else {
      setEditConfig(false);
      setSelectedConfigId(null);
      setConfigForm(emptyConfigForm);
    }
    setConfigModal(true);
  };

  const submitConfig = async () => {
    if (!configForm.inventory) {
      showToast('error', 'Validation', 'Select a hub / inventory');
      return;
    }
    if (!configForm.base_percentage || Number(configForm.base_percentage) < 0) {
      showToast('error', 'Validation', 'Base percentage is required');
      return;
    }

    const payload = {
      inventory: Number(configForm.inventory),
      is_enabled: !!configForm.is_enabled,
      base_percentage: Number(configForm.base_percentage),
      point_value: Number(configForm.point_value || 1),
      redemption_point_value: Number(configForm.redemption_point_value || 1),
      allow_redemption_on_products: !!configForm.allow_redemption_on_products,
      allow_redemption_on_subscriptions: !!configForm.allow_redemption_on_subscriptions,
      min_order_amount: Number(configForm.min_order_amount || 0),
      max_points_per_order: configForm.max_points_per_order
        ? Number(configForm.max_points_per_order)
        : null,
      expiry_days: configForm.expiry_days ? Number(configForm.expiry_days) : null,
      welcome_bonus_enabled: !!configForm.welcome_bonus_enabled,
      welcome_bonus_points: Number(configForm.welcome_bonus_points || 0),
    };

    try {
      setSaving(true);
      if (editConfig) {
        await CustomAxios.put(`loyalty-config/${selectedConfigId}/`, payload);
        showToast('success', 'Success', 'Loyalty config updated');
      } else {
        await CustomAxios.post('loyalty-config/', payload);
        showToast('success', 'Success', 'Loyalty config created');
      }
      setConfigModal(false);
      await fetchConfigs();
    } catch (e) {
      const detail =
        e.response?.data?.detail ||
        e.response?.data?.non_field_errors?.[0] ||
        e.response?.data?.inventory?.[0] ||
        (typeof e.response?.data === 'string' ? e.response.data : null) ||
        e.response?.data?.error ||
        'Save failed';
      showToast('error', 'Error', String(detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleConfigEnabled = async (item) => {
    const next = !item.is_enabled;
    await statusToggle.run(item.id, next, async () => {
      try {
        await CustomAxios.patch(`loyalty-config/${item.id}/`, {
          is_enabled: next,
        });
      } catch (e) {
        showToast('error', 'Error', 'Failed to update status');
        throw e;
      }
      showToast('success', 'Success', 'Status updated');
      await fetchConfigs();
    });
  };

  /*
   * A rule's own Active switch. PATCHes `is_active` on the rule -- the field the
   * edit form already sends -- and the list re-reads from the server, so what
   * it shows is what was saved. A failed save shows the error and the switch
   * falls back to the stored value.
   */
  /*
   * One reward of each type per store: turning a rule on switches off the
   * store's other rule of that type on the server, which names them in
   * `deactivated_rule_ids`. Say which, so the admin is not surprised by
   * switches that moved on their own.
   */
  const announceSwitchedOff = (data) => {
    const ids = Array.isArray(data?.deactivated_rule_ids) ? data.deactivated_rule_ids : [];
    if (!ids.length) return;
    const names = ids.map(
      (id) => redemptions.find((r) => String(r.id) === String(id))?.name || `#${id}`
    );
    const type = REDEMPTION_TYPES.find((t) => t.value === data?.redemption_type)?.label || 'reward';
    showToast('info', `One ${type} per store`, `Switched off: ${names.join(', ')}`);
  };

  const toggleRuleActive = async (row) => {
    const next = !row.is_active;
    await ruleStatusToggle.run(row.id, next, async () => {
      let res;
      try {
        res = await CustomAxios.patch(`loyalty-redemption-config/${row.id}/`, { is_active: next });
      } catch (e) {
        const fieldErrors = apiFieldErrors(e.response?.data);
        showToast(
          'error',
          'Error',
          e.response?.data?.error ||
            e.response?.data?.detail ||
            Object.values(fieldErrors)[0] ||
            'Failed to update status'
        );
        throw e;
      }
      showToast('success', 'Success', next ? 'Rule activated' : 'Rule deactivated');
      announceSwitchedOff(res?.data);
      await fetchRedemptions();
    });
  };

  const deleteConfig = async (id) => {
    if (!window.confirm('Delete this loyalty config and its redemptions?')) return;
    try {
      setLoading(true);
      await CustomAxios.delete(`loyalty-config/${id}/`);
      showToast('success', 'Success', 'Deleted');
      await fetchConfigs();
      await fetchRedemptions();
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.error || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const backfillWelcome = async (row, dryRun) => {
    const hub = hubLabel(row.inventory);
    const points = row.welcome_bonus_points ?? 0;
    if (!row.welcome_bonus_enabled || !points) {
      showToast(
        'warn',
        'Welcome bonus',
        'Enable welcome bonus and set points on this config before backfill'
      );
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        `Credit ${points} welcome points to eligible past users for ${hub}?\n\n` +
          'This is idempotent (one WELCOME_BONUS per user). Prefer dry-run first.'
      );
      if (!ok) return;
    }
    try {
      setLoading(true);
      const res = await CustomAxios.post(`loyalty-config/${row.id}/backfill-welcome/`, {
        dry_run: !!dryRun,
      });
      const d = res.data || {};
      if (d.status !== 'ok') {
        showToast('error', 'Backfill', d.reason || d.error || 'Failed');
        return;
      }
      const detail = dryRun
        ? `Dry-run ${hub}: scanned=${d.scanned}, would_credit=${d.would_credit ?? d.eligible}, already=${d.already_credited}, no_hub=${d.skipped_no_hub}, other_hub=${d.skipped_wrong_hub}`
        : `Backfill ${hub}: credited=${d.credited}, already=${d.already_credited}, scanned=${d.scanned}, errors=${d.errors}`;
      showToast('success', dryRun ? 'Dry-run complete' : 'Backfill complete', detail);
    } catch (e) {
      showToast(
        'error',
        'Backfill',
        e.response?.data?.reason || e.response?.data?.error || 'Backfill failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const openRedemptionModal = (item = null) => {
    if (item) {
      setEditRedemption(true);
      setSelectedRedemptionId(item.id);
      setRedemptionForm(redemptionToForm(item));
    } else {
      setEditRedemption(false);
      setSelectedRedemptionId(null);
      setRedemptionForm({
        ...emptyRedemptionForm,
        loyalty_config: configs[0]?.id || '',
      });
    }
    setRedemptionErrors({});
    setRedemptionModal(true);
  };

  const submitRedemption = async () => {
    const errors = validateRedemptionForm(redemptionForm);
    setRedemptionErrors(errors);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      showToast('error', 'Validation', firstError);
      return;
    }

    const payload = buildRedemptionPayload(redemptionForm);

    try {
      setSaving(true);
      let res;
      if (editRedemption) {
        res = await CustomAxios.put(`loyalty-redemption-config/${selectedRedemptionId}/`, payload);
        showToast('success', 'Success', 'Redemption rule updated');
      } else {
        res = await CustomAxios.post('loyalty-redemption-config/', payload);
        showToast('success', 'Success', 'Redemption rule created');
      }
      // Saved is not the same as offered: say so now, not after a customer asks.
      const hiddenReason = hiddenFromCustomersReason(res?.data);
      if (hiddenReason) showToast('warn', 'Saved, but hidden from customers', hiddenReason);
      announceSwitchedOff(res?.data);
      setRedemptionModal(false);
      await fetchRedemptions();
    } catch (e) {
      const fieldErrors = apiFieldErrors(e.response?.data);
      setRedemptionErrors(fieldErrors);
      showToast(
        'error',
        'Error',
        e.response?.data?.error ||
          e.response?.data?.detail ||
          Object.values(fieldErrors)[0] ||
          'Save failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteRedemption = async (id) => {
    if (!window.confirm('Delete this redemption rule?')) return;
    try {
      await CustomAxios.delete(`loyalty-redemption-config/${id}/`);
      showToast('success', 'Success', 'Deleted');
      fetchRedemptions();
    } catch (e) {
      showToast('error', 'Error', 'Delete failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toast ref={toast} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Loyalty Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Hub earn rates and point redemption. For subscriber 2x points, free delivery, and every-Nth-order
          random offers, configure those on Subscription Plans.
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Earn Config (Hub)" />
        <Tab label="Redemption Rules" />
      </Tabs>

      {loading && !configs.length && !redemptions.length ? (
        <GrowthPageLoader label="Loading loyalty settings…" />
      ) : null}

      {!(loading && !configs.length && !redemptions.length) && tab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <GrowthPrimaryButton startIcon={<AddIcon />} onClick={() => openConfigModal()}>
              Add Hub Loyalty Config
            </GrowthPrimaryButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hub</TableCell>
                  <TableCell>Base %</TableCell>
                  <TableCell>Earn ₹/pt</TableCell>
                  <TableCell>Redeem ₹/pt</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell>Subscriptions</TableCell>
                  <TableCell>Min Order</TableCell>
                  <TableCell>Max Pts / Order</TableCell>
                  <TableCell>Expiry Days</TableCell>
                  <TableCell>Welcome Pts</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {loading ? 'Loading…' : 'No loyalty configs yet'}
                    </TableCell>
                  </TableRow>
                )}
                {configs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{hubLabel(row.inventory)}</TableCell>
                    <TableCell>{row.base_percentage}%</TableCell>
                    <TableCell>{row.point_value}</TableCell>
                    <TableCell>{row.redemption_point_value ?? row.point_value}</TableCell>
                    <TableCell>{row.allow_redemption_on_products !== false ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.allow_redemption_on_subscriptions ? 'Yes' : 'No'}</TableCell>
                    <TableCell>₹{row.min_order_amount}</TableCell>
                    <TableCell>{row.max_points_per_order ?? '—'}</TableCell>
                    <TableCell>{row.expiry_days ?? '—'}</TableCell>
                    <TableCell>
                      {row.welcome_bonus_enabled
                        ? `${row.welcome_bonus_points ?? 0}`
                        : 'Off'}
                    </TableCell>
                    <TableCell>
                      <StatusSwitch
                        checked={!!statusToggle.displayed(row.id, row.is_enabled)}
                        pending={statusToggle.isPending(row.id)}
                        onChange={() => toggleConfigEnabled(row)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openConfigModal(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        sx={{ textTransform: 'none', minWidth: 0, mr: 0.5 }}
                        onClick={() => backfillWelcome(row, true)}
                        disabled={loading}
                      >
                        Dry-run
                      </Button>
                      <Button
                        size="small"
                        sx={{ textTransform: 'none', minWidth: 0, mr: 0.5 }}
                        onClick={() => backfillWelcome(row, false)}
                        disabled={loading}
                      >
                        Backfill
                      </Button>
                      <IconButton size="small" color="error" onClick={() => deleteConfig(row.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!(loading && !configs.length && !redemptions.length) && tab === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <GrowthPrimaryButton
              startIcon={<AddIcon />}
              onClick={() => openRedemptionModal()}
              disabled={!configs.length}
            >
              Add Redemption Rule
            </GrowthPrimaryButton>
          </Box>
          {!configs.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a hub earn config first, then add redemption rules.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One rule of each type can be active per store at a time, and customers use
            only one reward per order. Turning a rule on switches off that store&apos;s other
            rule of the same type. Free Product rules are the exception: each maps one
            loyalty product, and any number can be active together.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Hub Config</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Min Points</TableCell>
                  <TableCell>Max Discount %</TableCell>
                  <TableCell>Max ₹ Cap</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No redemption rules
                    </TableCell>
                  </TableRow>
                )}
                {redemptions.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{configLabel(row.loyalty_config)}</TableCell>
                    <TableCell>
                      {REDEMPTION_TYPES.find((t) => t.value === row.redemption_type)?.label ||
                        row.redemption_type}
                      {(row.offer_name || row.product_name) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.offer_name || row.product_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.min_points_required}</TableCell>
                    <TableCell>{row.max_discount_percentage ?? '—'}</TableCell>
                    <TableCell>{row.max_redemption_value != null ? `₹${row.max_redemption_value}` : '—'}</TableCell>
                    <TableCell>
                      {/*
                        The rule's OWN switch. It used to be a "Yes/No" that
                        also went "No" whenever the hub was off -- so an active
                        rule read as inactive, and nothing on this tab could
                        change it. The hub is a separate switch (Earn Config
                        tab) and is shown as a separate note.
                      */}
                      <StatusSwitch
                        checked={!!ruleStatusToggle.displayed(row.id, row.is_active)}
                        pending={ruleStatusToggle.isPending(row.id)}
                        onChange={() => toggleRuleActive(row)}
                      />
                      {!configEnabled(row.loyalty_config) && (
                        <Typography variant="caption" color="error" display="block">
                          Hub loyalty off: not offered to customers. Turn it on in Earn Config.
                        </Typography>
                      )}
                      {configEnabled(row.loyalty_config) && hiddenFromCustomersReason(row) && (
                        <Typography variant="caption" color="error" display="block" sx={{ maxWidth: 260 }}>
                          {hiddenFromCustomersReason(row)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openRedemptionModal(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteRedemption(row.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Earn config modal */}
      <GrowthModal
        open={configModal}
        onClose={() => !saving && setConfigModal(false)}
        title={editConfig ? 'Edit Loyalty Config' : 'Add Loyalty Config'}
        subtitle="Hub earn rates, welcome bonus and redemption toggles"
        busy={saving}
        busyLabel={editConfig ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setConfigModal(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={submitConfig} loading={saving}>
              {editConfig ? 'Update' : 'Create'}
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={editConfig}>
                <InputLabel>Hub</InputLabel>
                <Select
                  label="Hub"
                  value={configForm.inventory}
                  onChange={(e) => setConfigForm({ ...configForm, inventory: e.target.value })}
                >
                  {inventoryList.map((hub) => (
                    <MenuItem key={hub.value ?? hub.id} value={hub.value ?? hub.id}>
                      {hub.label || hub.place || hub.name || `Hub ${hub.value ?? hub.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <GrowthFormSection title="Earn rates" />
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Base earn %"
                type="number"
                value={configForm.base_percentage}
                onChange={(e) => setConfigForm({ ...configForm, base_percentage: e.target.value })}
                helperText="Order amount × % → points (before subscription multiplier)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Earn point value (₹ per point earned)"
                type="number"
                value={configForm.point_value}
                onChange={(e) => setConfigForm({ ...configForm, point_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Redeem point value (₹ per point redeemed)"
                type="number"
                value={configForm.redemption_point_value}
                onChange={(e) =>
                  setConfigForm({ ...configForm, redemption_point_value: e.target.value })
                }
                helperText="Separate from earn rate — used at checkout redemption"
              />
            </Grid>

            <GrowthFormSection title="Redemption options" />
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Allow redemption on products"
                checked={configForm.allow_redemption_on_products}
                onChange={(e) =>
                  setConfigForm({ ...configForm, allow_redemption_on_products: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Allow redemption on subscriptions"
                checked={configForm.allow_redemption_on_subscriptions}
                onChange={(e) =>
                  setConfigForm({ ...configForm, allow_redemption_on_subscriptions: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Min order amount"
                type="number"
                value={configForm.min_order_amount}
                onChange={(e) => setConfigForm({ ...configForm, min_order_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Max points / order"
                type="number"
                value={configForm.max_points_per_order}
                onChange={(e) =>
                  setConfigForm({ ...configForm, max_points_per_order: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Expiry days"
                type="number"
                value={configForm.expiry_days}
                onChange={(e) => setConfigForm({ ...configForm, expiry_days: e.target.value })}
              />
            </Grid>

            <GrowthFormSection title="Welcome loyalty bonus (signup + optional backfill)" />
            <Grid item xs={12} sm={6}>
              <GrowthFormSwitch
                label="Welcome bonus enabled"
                checked={configForm.welcome_bonus_enabled}
                onChange={(e) =>
                  setConfigForm({ ...configForm, welcome_bonus_enabled: e.target.checked })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Welcome bonus points"
                type="number"
                value={configForm.welcome_bonus_points}
                onChange={(e) =>
                  setConfigForm({ ...configForm, welcome_bonus_points: e.target.value })
                }
                helperText="Credited once per user (WELCOME_BONUS). Future credits only."
              />
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Enabled"
                checked={configForm.is_enabled}
                onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>

      {/* Redemption modal */}
      <GrowthModal
        open={redemptionModal}
        onClose={() => !saving && setRedemptionModal(false)}
        title={editRedemption ? 'Edit Redemption Rule' : 'Add Redemption Rule'}
        subtitle={TYPE_SUBTITLES[redemptionForm.redemption_type] || TYPE_SUBTITLES.PURCHASE_DISCOUNT}
        busy={saving}
        busyLabel={editRedemption ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setRedemptionModal(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={submitRedemption} loading={saving}>
              {editRedemption ? 'Update' : 'Create'}
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!redemptionErrors.loyalty_config}>
                <InputLabel>Loyalty config</InputLabel>
                <Select
                  label="Loyalty config"
                  value={redemptionForm.loyalty_config}
                  onChange={(e) =>
                    // Offers belong to a hub, so a different config means a
                    // different list to pick from.
                    updateRedemptionForm({ loyalty_config: e.target.value, offer: '' })
                  }
                >
                  {configs.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {hubLabel(c.inventory)} (#{c.id})
                    </MenuItem>
                  ))}
                </Select>
                {redemptionErrors.loyalty_config && (
                  <FormHelperText>{redemptionErrors.loyalty_config}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!redemptionErrors.redemption_type}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={redemptionForm.redemption_type}
                  onChange={(e) => onRedemptionTypeChange(e.target.value)}
                >
                  {REDEMPTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
                {redemptionErrors.redemption_type && (
                  <FormHelperText>{redemptionErrors.redemption_type}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={redemptionForm.redemption_type === 'OFFER' ? 'Offer / rule name' : 'Name'}
                value={redemptionForm.name}
                onChange={(e) => updateRedemptionForm({ name: e.target.value })}
                {...fieldError('name')}
              />
            </Grid>
            {usesField(redemptionForm.redemption_type, 'offer') && (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!redemptionErrors.offer}
                  disabled={!redemptionHub || offersLoading}
                >
                  <InputLabel>{mapsLoyaltyProduct ? 'Loyalty product' : 'Offer'}</InputLabel>
                  <Select
                    label={mapsLoyaltyProduct ? 'Loyalty product' : 'Offer'}
                    value={offersLoading ? '' : redemptionForm.offer}
                    onChange={(e) => updateRedemptionForm({ offer: e.target.value })}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {pickableOffers.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {mapsLoyaltyProduct && o.offer_products?.item_name
                          ? `${o.offer_products.item_name} (${o.offer_name || o.offer_id})`
                          : o.offer_name || o.offer_id}
                        {mapsLoyaltyProduct ? ` · ${o.available_quantity ?? 0} left` : ''}
                        {o.is_active ? '' : ' (inactive)'}
                      </MenuItem>
                    ))}
                    {/* A saved offer no longer on this hub stays selectable, so edit does not silently drop it. */}
                    {redemptionForm.offer !== '' &&
                      !pickableOffers.some((o) => Number(o.id) === Number(redemptionForm.offer)) && (
                        <MenuItem value={redemptionForm.offer}>
                          {(mapsLoyaltyProduct ? selectedRedemption?.product_name : null) ||
                            selectedRedemption?.offer_name ||
                            `Offer #${redemptionForm.offer}`}
                        </MenuItem>
                      )}
                  </Select>
                  <FormHelperText>
                    {redemptionErrors.offer ||
                      (offersLoading
                        ? (mapsLoyaltyProduct ? 'Loading loyalty products…' : 'Loading offers…')
                        : !pickableOffers.length
                        ? (mapsLoyaltyProduct
                            ? 'No loyalty products on this hub yet. Add one in Offer Management → Loyalty Products.'
                            : 'No offers on this hub yet. Create one under Offers first.')
                        : mapsLoyaltyProduct
                        ? 'From Offer Management → Loyalty Products. Its quantity, stores and dates come from there.'
                        : 'The offer customers unlock with their points')}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            {usesField(redemptionForm.redemption_type, 'product') && (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!redemptionErrors.product}
                  disabled={productsLoading}
                >
                  <InputLabel>Product</InputLabel>
                  <Select
                    label="Product"
                    value={productsLoading ? '' : redemptionForm.product}
                    onChange={(e) => updateRedemptionForm({ product: e.target.value })}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {productOptions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.item_name}
                      </MenuItem>
                    ))}
                    {redemptionForm.product !== '' &&
                      !productOptions.some((p) => Number(p.id) === Number(redemptionForm.product)) && (
                        <MenuItem value={redemptionForm.product}>
                          {selectedRedemption?.product_name || `Product #${redemptionForm.product}`}
                        </MenuItem>
                      )}
                  </Select>
                  <FormHelperText>
                    {redemptionErrors.product ||
                      (productsLoading ? 'Loading products…' : 'The product customers get free')}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Description"
                value={redemptionForm.description}
                onChange={(e) => updateRedemptionForm({ description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={
                  redemptionForm.redemption_type === 'PURCHASE_DISCOUNT'
                    ? 'Min points required'
                    : 'Points required'
                }
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={redemptionForm.min_points_required}
                onChange={(e) => updateRedemptionForm({ min_points_required: e.target.value })}
                {...fieldError(
                  'min_points_required',
                  redemptionForm.redemption_type === 'PURCHASE_DISCOUNT'
                    ? undefined
                    : 'Points deducted when a customer redeems this'
                )}
              />
            </Grid>
            {usesField(redemptionForm.redemption_type, 'max_points_allowed') && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max points allowed"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={redemptionForm.max_points_allowed}
                  onChange={(e) => updateRedemptionForm({ max_points_allowed: e.target.value })}
                  {...fieldError('max_points_allowed')}
                />
              </Grid>
            )}
            {usesField(redemptionForm.redemption_type, 'max_discount_percentage') && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max discount %"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={redemptionForm.max_discount_percentage}
                  onChange={(e) =>
                    updateRedemptionForm({ max_discount_percentage: e.target.value })
                  }
                  {...fieldError('max_discount_percentage')}
                />
              </Grid>
            )}
            {usesField(redemptionForm.redemption_type, 'max_redemption_value') && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={REDEMPTION_VALUE_LABELS[redemptionForm.redemption_type]?.label}
                  type="number"
                  inputProps={{ min: 0 }}
                  value={redemptionForm.max_redemption_value}
                  onChange={(e) => updateRedemptionForm({ max_redemption_value: e.target.value })}
                  {...fieldError(
                    'max_redemption_value',
                    redemptionForm.redemption_type === 'PURCHASE_DISCOUNT'
                      ? undefined
                      : REDEMPTION_VALUE_LABELS[redemptionForm.redemption_type]?.helperText
                  )}
                />
              </Grid>
            )}
            {usesField(redemptionForm.redemption_type, 'valid_from') && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Valid from"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={redemptionForm.valid_from}
                    onChange={(e) => updateRedemptionForm({ valid_from: e.target.value })}
                    {...fieldError('valid_from', 'Leave blank to start now')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Valid until"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={redemptionForm.valid_until}
                    onChange={(e) => updateRedemptionForm({ valid_until: e.target.value })}
                    {...fieldError('valid_until', 'Leave blank to never expire')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Total redemption limit"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={redemptionForm.max_redemptions_total}
                    onChange={(e) => updateRedemptionForm({ max_redemptions_total: e.target.value })}
                    {...fieldError('max_redemptions_total', 'Across all customers. Blank = unlimited')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Limit per customer"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={redemptionForm.max_redemptions_per_user}
                    onChange={(e) => updateRedemptionForm({ max_redemptions_per_user: e.target.value })}
                    {...fieldError('max_redemptions_per_user', 'Per customer. Blank = unlimited')}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Active"
                checked={redemptionForm.is_active}
                onChange={(e) => updateRedemptionForm({ is_active: e.target.checked })}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {redemptionForm.redemption_type === 'PRODUCT' ? (
                  'Free Product rules can be active together, one per loyalty product. Saving this rule switches nothing else off.'
                ) : (
                  <>
                    One rule of each type can be active per store at a time, and customers use
                    only one reward per order. Saving this rule as active switches off the
                    store&apos;s other {REDEMPTION_TYPES.find((t) => t.value === redemptionForm.redemption_type)?.label || 'rule'} rule.
                  </>
                )}
              </Typography>
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default LoyaltyConfig;
